import type { ApiClient } from './ApiClient';

// Forme alignée sur POST/GET/PATCH /schools/:id/payment-methods[...] (PAY-18,
// apps/api/src/routes/schoolPaymentMethods.ts) — seule source de vérité pour ces
// routes. JAMAIS un identifiant/une clé en clair dans ce type : le numéro Wave/
// Orange Money est stocké EN CLAIR côté serveur (décision du ticket) donc renvoyé
// tel quel, les identifiants PSP restent chiffrés au repos et ne reviennent
// JAMAIS sur le fil — uniquement `apiKeyConfigured`/`secretKeyConfigured`.
export type SchoolPaymentMethodType = 'WAVE' | 'ORANGE_MONEY' | 'PSP_CARD';

export interface SchoolPaymentMethod {
  id: string;
  type: SchoolPaymentMethodType;
  enabled: boolean;
  phoneNumber: string | null;
  apiKeyConfigured: boolean;
  secretKeyConfigured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListSchoolPaymentMethodsResponse {
  paymentMethods: SchoolPaymentMethod[];
}

// École sans configuration -> `paymentMethods: []`, jamais une erreur (même règle
// métier que le catalogue de fournitures, FOU-01/le catalogue de réductions,
// PAY-10).
export function listSchoolPaymentMethods(
  client: ApiClient,
  schoolId: string,
  accessToken: string,
): Promise<ListSchoolPaymentMethodsResponse> {
  return client.get<ListSchoolPaymentMethodsResponse>(`/schools/${schoolId}/payment-methods`, { accessToken });
}

export interface CreateSchoolPaymentMethodPayload {
  type: SchoolPaymentMethodType;
  enabled?: boolean;
  phoneNumber?: string;
  apiKey?: string;
  secretKey?: string;
}

export interface CreateSchoolPaymentMethodResponse {
  paymentMethod: SchoolPaymentMethod;
}

export function createSchoolPaymentMethod(
  client: ApiClient,
  schoolId: string,
  payload: CreateSchoolPaymentMethodPayload,
  accessToken: string,
): Promise<CreateSchoolPaymentMethodResponse> {
  return client.post<CreateSchoolPaymentMethodResponse>(`/schools/${schoolId}/payment-methods`, payload, { accessToken });
}

export interface UpdateSchoolPaymentMethodPayload {
  enabled?: boolean;
  phoneNumber?: string;
  apiKey?: string;
  secretKey?: string;
}

export interface UpdateSchoolPaymentMethodResponse {
  paymentMethod: SchoolPaymentMethod;
}

// Champs OMIS = INCHANGÉS (comportement PARTIEL, même convention que `PATCH
// /students/:id`, SCH-12) — permet de désactiver un moyen de paiement SANS
// ressaisir le numéro/les clés déjà en base (cas limite explicite du ticket :
// changer de numéro en cours d'année ne doit jamais forcer une reconfiguration
// complète).
export function updateSchoolPaymentMethod(
  client: ApiClient,
  schoolId: string,
  type: SchoolPaymentMethodType,
  payload: UpdateSchoolPaymentMethodPayload,
  accessToken: string,
): Promise<UpdateSchoolPaymentMethodResponse> {
  return client.patch<UpdateSchoolPaymentMethodResponse>(`/schools/${schoolId}/payment-methods/${type}`, payload, { accessToken });
}

// Endpoint PARENT-FACING (GET /parents/students/:studentId/payment-methods) —
// UNIQUEMENT des booléens, jamais le détail complet ci-dessus. Hors périmètre
// direct de ce ticket (aucun écran web-parent ne le consomme encore, réservé à
// PAY-04/16) mais exposé ici pour que ces futurs tickets n'aient pas à
// redéfinir ce contrat.
export interface AvailablePaymentMethods {
  WAVE: boolean;
  ORANGE_MONEY: boolean;
  PSP_CARD: boolean;
}

export interface GetAvailablePaymentMethodsResponse {
  availableMethods: AvailablePaymentMethods;
}

export function getAvailablePaymentMethods(
  client: ApiClient,
  studentId: string,
  accessToken: string,
): Promise<GetAvailablePaymentMethodsResponse> {
  return client.get<GetAvailablePaymentMethodsResponse>(`/parents/students/${studentId}/payment-methods`, { accessToken });
}
